import { cn } from '@/lib/utils'
import {
  CheckmarkFilled as CheckCircle2,
  Information as Info,
  Idea as Lightbulb,
  Rocket,
  AiGenerate as Sparkles,
  Growth as TrendingUp,
  Lightning as Zap,
} from '@carbon/icons-react'

interface EnhancedCalloutProps {
  type?: 'info' | 'success' | 'warning' | 'idea' | 'milestone' | 'highlight'
  title?: string
  children: React.ReactNode
  icon?: string
  className?: string
}

// Borderless tinted panels — the wash carries the type, slightly stronger
// than before since there is no outline to help it.
const typeStyles = {
  info: {
    bg: 'bg-blue-500/[0.07]',
    icon: 'text-blue-600 dark:text-blue-400',
    IconComponent: Info,
  },
  success: {
    bg: 'bg-emerald-600/[0.07] dark:bg-primary-active/[0.07]',
    icon: 'text-emerald-600 dark:text-primary-active',
    IconComponent: CheckCircle2,
  },
  warning: {
    bg: 'bg-amber-500/[0.07]',
    icon: 'text-amber-600 dark:text-amber-400',
    IconComponent: Zap,
  },
  idea: {
    bg: 'bg-purple-500/[0.07]',
    icon: 'text-purple-600 dark:text-purple-400',
    IconComponent: Lightbulb,
  },
  milestone: {
    bg: 'bg-emerald-600/[0.07] dark:bg-primary-active/[0.07]',
    icon: 'text-emerald-600 dark:text-primary-active',
    IconComponent: Rocket,
  },
  highlight: {
    bg: 'bg-pink-500/[0.07]',
    icon: 'text-pink-600 dark:text-pink-400',
    IconComponent: Sparkles,
  },
}

export function EnhancedCallout({
  type = 'info',
  title,
  children,
  icon,
  className,
}: EnhancedCalloutProps) {
  const style = typeStyles[type]
  const IconComponent = style.IconComponent

  return (
    <div className={cn('rounded-xl p-5 my-6 sm:p-6', style.bg, className)}>
      <div className="flex gap-3.5">
        {/* Bare icon — no chip, no ring; the tint colour is the marker */}
        <IconComponent className={cn('mt-0.5 size-5 shrink-0', style.icon)} />

        {/* Content */}
        <div className="flex-1">
          {title && (
            <div className="text-base font-semibold mb-2 text-foreground">
              {title}
            </div>
          )}
          <div className="text-sm text-muted-foreground leading-relaxed">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

interface HighlightBoxProps {
  children: React.ReactNode
  color?: string
  icon?: React.ComponentType<{ className?: string }>
}

export function HighlightBox({
  children,
  color,
  icon: Icon,
}: HighlightBoxProps) {
  return (
    <div className="my-8 rounded-xl bg-muted p-6 dark:bg-muted/60">
      <div className="text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </div>
  )
}

export function QuoteBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-8 rounded-xl bg-muted p-6 dark:bg-muted/60 sm:p-8">
      <div className="text-xl font-medium italic leading-relaxed text-foreground">
        {children}
      </div>
    </div>
  )
}
